import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Archive, ContentCopy, Edit, Refresh, Unarchive } from '@mui/icons-material';
import { platformApi, BookDesignTemplate } from '../services/platformApi';
import {
  BookDesignFormState,
  BookFontRole,
  applyDesignSettingsToForm,
  bookFontLabels,
  bookFontRoles,
  buildBookDesignSettings,
  createBookDesignForm,
} from './project-workspace/bookDesignUtils';
import { getStatusColor } from './projectConstants';
import { useLanguage } from '../LanguageContext';
import { formatTranslatedLabel } from '../i18n';

type BookType = 'text' | 'picture' | 'mixed';

interface TemplateMetaState {
  name: string;
  description: string;
  bookType: BookType;
}

const initialMeta: TemplateMetaState = { name: '', description: '', bookType: 'text' };

const languageChoices = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'he', label: 'עברית' },
  { value: 'ja', label: '日本語' },
];

const getRequestErrorMessage = (err: unknown, fallback: string): string => {
  const responseData =
    typeof err === 'object' && err !== null && 'response' in err
      ? (err as { response?: { data?: { message?: unknown; error?: unknown } } }).response?.data
      : undefined;
  if (typeof responseData?.message === 'string') {
    return responseData.message;
  }
  if (typeof responseData?.error === 'string') {
    return responseData.error;
  }
  return fallback;
};

// Reuse the same design_settings_json shape (and its overlay function) that
// the project design studio uses, so a template is guaranteed to produce
// settings a book can actually apply.
const templateToDesignForm = (template: BookDesignTemplate): BookDesignFormState =>
  applyDesignSettingsToForm(createBookDesignForm(), template.design_settings_json, {
    language: template.language || undefined,
    textDirection: template.text_direction || undefined,
  });

const DesignTemplateManagement: React.FC = () => {
  const { t } = useLanguage();
  const [templates, setTemplates] = useState<BookDesignTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [dialogTab, setDialogTab] = useState<'basic' | 'print' | 'fonts' | 'rules'>('basic');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [meta, setMeta] = useState<TemplateMetaState>(initialMeta);
  const [form, setForm] = useState<BookDesignFormState>(createBookDesignForm());
  const [saving, setSaving] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setTemplates(await platformApi.listBookDesignTemplates());
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotLoadDesignTemplates')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const openCreateDialog = () => {
    setEditingId(null);
    setMeta(initialMeta);
    setForm(createBookDesignForm());
    setDialogError(null);
    setDialogTab('basic');
    setDialogOpen(true);
  };

  const openEditDialog = (template: BookDesignTemplate) => {
    setEditingId(template.id);
    setMeta({
      name: template.name,
      description: template.description || '',
      bookType: (template.book_type as BookType) || 'text',
    });
    setForm(templateToDesignForm(template));
    setDialogError(null);
    setDialogTab('basic');
    setDialogOpen(true);
  };

  // Create a new template pre-filled from an existing one. Leaves editingId
  // null so Save creates a fresh template rather than overwriting the source.
  const openDuplicateDialog = (template: BookDesignTemplate) => {
    setEditingId(null);
    setMeta({
      name: `${template.name} (${t('copyNoun')})`,
      description: template.description || '',
      bookType: (template.book_type as BookType) || 'text',
    });
    setForm(templateToDesignForm(template));
    setDialogError(null);
    setDialogTab('basic');
    setDialogOpen(true);
  };

  const setField = (field: keyof BookDesignFormState, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const setSpacingField = (field: keyof BookDesignFormState['spacing'], value: string) => {
    setForm((current) => ({ ...current, spacing: { ...current.spacing, [field]: value } }));
  };

  const setFontField = (
    role: BookFontRole,
    field: keyof BookDesignFormState['fonts'][BookFontRole],
    value: string | boolean
  ) => {
    setForm((current) => ({
      ...current,
      fonts: { ...current.fonts, [role]: { ...current.fonts[role], [field]: value } },
    }));
  };

  const handleSave = async () => {
    const name = meta.name.trim();
    if (!name) {
      setDialogError(t('templateNameRequired'));
      return;
    }
    try {
      setSaving(true);
      setDialogError(null);
      const payload = {
        name,
        description: meta.description.trim() || undefined,
        book_type: meta.bookType,
        language: form.language,
        text_direction: form.textDirection as 'ltr' | 'rtl',
        design_settings_json: buildBookDesignSettings(form),
      };
      if (editingId) {
        await platformApi.updateBookDesignTemplate(editingId, payload);
        setSuccess(t('designTemplateUpdated'));
      } else {
        await platformApi.createBookDesignTemplate(payload);
        setSuccess(t('designTemplateCreated'));
      }
      setDialogOpen(false);
      await loadTemplates();
    } catch (err) {
      setDialogError(getRequestErrorMessage(err, t('couldNotSaveDesignTemplate')));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template: BookDesignTemplate) => {
    try {
      setError(null);
      setSuccess(null);
      await platformApi.updateBookDesignTemplate(template.id, { is_active: !template.is_active });
      await loadTemplates();
    } catch (err) {
      setError(getRequestErrorMessage(err, t('couldNotSaveDesignTemplate')));
    }
  };

  const numberField = (label: string, value: string, onChange: (value: string) => void, unit?: string) => (
    <TextField
      label={label}
      type="number"
      size="small"
      fullWidth
      value={value}
      onChange={(e) => onChange(e.target.value)}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t('designTemplates')}
          </Typography>
          <Typography color="text.secondary">{t('designTemplatesDescription')}</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton aria-label={t('refresh')} onClick={loadTemplates}>
            <Refresh />
          </IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
            {t('newDesignTemplate')}
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('name')}</TableCell>
              <TableCell>{t('bookType')}</TableCell>
              <TableCell>{t('language')}</TableCell>
              <TableCell>{t('status')}</TableCell>
              <TableCell align="right">{t('actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id} hover>
                <TableCell>
                  <Typography fontWeight={700}>{template.name}</Typography>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatTranslatedLabel(t, template.book_type)}</TableCell>
                <TableCell>{template.language || t('notSet')}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={template.is_active ? t('active') : t('archived')}
                    color={getStatusColor(template.is_active ? 'active' : 'archived')}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('duplicate')}>
                    <IconButton size="small" onClick={() => openDuplicateDialog(template)}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('edit')}>
                    <IconButton size="small" onClick={() => openEditDialog(template)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={template.is_active ? t('archive') : t('restore')}>
                    <IconButton size="small" onClick={() => handleToggleActive(template)}>
                      {template.is_active ? <Archive fontSize="small" /> : <Unarchive fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && templates.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    {t('noDesignTemplatesYet')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingId ? t('editDesignTemplate') : t('newDesignTemplate')}</DialogTitle>
        <DialogContent dividers>
          {dialogError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dialogError}
            </Alert>
          )}
          <Tabs value={dialogTab} onChange={(_e, value) => setDialogTab(value)} sx={{ mb: 2 }}>
            <Tab value="basic" label={t('basicInfo')} />
            <Tab value="print" label={t('printParameters')} />
            <Tab value="fonts" label={t('fonts')} />
            <Tab value="rules" label={t('indentationAndRules')} />
          </Tabs>

          {dialogTab === 'basic' && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('name')}
                  fullWidth
                  value={meta.name}
                  onChange={(e) => setMeta((current) => ({ ...current, name: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('bookType')}
                  select
                  fullWidth
                  value={meta.bookType}
                  onChange={(e) => setMeta((current) => ({ ...current, bookType: e.target.value as BookType }))}
                >
                  <MenuItem value="text">{formatTranslatedLabel(t, 'text')}</MenuItem>
                  <MenuItem value="picture">{formatTranslatedLabel(t, 'picture')}</MenuItem>
                  <MenuItem value="mixed">{formatTranslatedLabel(t, 'mixed')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('description')}
                  fullWidth
                  multiline
                  minRows={2}
                  value={meta.description}
                  onChange={(e) => setMeta((current) => ({ ...current, description: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('language')}
                  select
                  fullWidth
                  value={form.language}
                  onChange={(e) => setField('language', e.target.value)}
                >
                  {languageChoices.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('direction')}
                  select
                  fullWidth
                  value={form.textDirection}
                  onChange={(e) => setField('textDirection', e.target.value)}
                >
                  <MenuItem value="ltr">{t('leftToRight')}</MenuItem>
                  <MenuItem value="rtl">{t('rightToLeft')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}

          {dialogTab === 'print' && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                {numberField(t('widthCm'), form.pageWidthCm, (v) => setField('pageWidthCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('heightCm'), form.pageHeightCm, (v) => setField('pageHeightCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('innerMarginCm'), form.marginInnerCm, (v) => setField('marginInnerCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('outerMarginCm'), form.marginOuterCm, (v) => setField('marginOuterCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('topMarginCm'), form.marginTopCm, (v) => setField('marginTopCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('bottomMarginCm'), form.marginBottomCm, (v) => setField('marginBottomCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('startPage'), form.startPageNumber, (v) => setField('startPageNumber', v))}
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t('textAlign')}
                  select
                  size="small"
                  fullWidth
                  value={form.textAlignment}
                  onChange={(e) => setField('textAlignment', e.target.value)}
                >
                  <MenuItem value="left">{formatTranslatedLabel(t, 'left')}</MenuItem>
                  <MenuItem value="right">{formatTranslatedLabel(t, 'right')}</MenuItem>
                  <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                  <MenuItem value="justify">{formatTranslatedLabel(t, 'justify')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  {t('headers')}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('rightPageHeader')}
                  fullWidth
                  size="small"
                  value={form.rightHeaderContent}
                  onChange={(e) => setField('rightHeaderContent', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('leftPageHeader')}
                  fullWidth
                  size="small"
                  value={form.leftHeaderContent}
                  onChange={(e) => setField('leftHeaderContent', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('rightHeaderTopCm'), form.rightHeaderTopMarginCm, (v) => setField('rightHeaderTopMarginCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t('rightHeaderAlign')}
                  select
                  size="small"
                  fullWidth
                  value={form.rightHeaderAlignment}
                  onChange={(e) => setField('rightHeaderAlignment', e.target.value)}
                >
                  <MenuItem value="outer">{formatTranslatedLabel(t, 'outer')}</MenuItem>
                  <MenuItem value="inner">{formatTranslatedLabel(t, 'inner')}</MenuItem>
                  <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} md={3}>
                {numberField(t('leftHeaderTopCm'), form.leftHeaderTopMarginCm, (v) => setField('leftHeaderTopMarginCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  label={t('leftHeaderAlign')}
                  select
                  size="small"
                  fullWidth
                  value={form.leftHeaderAlignment}
                  onChange={(e) => setField('leftHeaderAlignment', e.target.value)}
                >
                  <MenuItem value="outer">{formatTranslatedLabel(t, 'outer')}</MenuItem>
                  <MenuItem value="inner">{formatTranslatedLabel(t, 'inner')}</MenuItem>
                  <MenuItem value="center">{formatTranslatedLabel(t, 'center')}</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}

          {dialogTab === 'fonts' && (
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('element')}</TableCell>
                    <TableCell>{t('font')}</TableCell>
                    <TableCell>{t('size')}</TableCell>
                    <TableCell>{t('lineHeight')}</TableCell>
                    <TableCell>{t('color')}</TableCell>
                    <TableCell>{t('style')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookFontRoles.map((role) => {
                    const font = form.fonts[role];
                    return (
                      <TableRow key={role} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{bookFontLabels[role]}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={font.family}
                            onChange={(e) => setFontField(role, 'family', e.target.value)}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 100 }}>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={font.sizePt}
                            onChange={(e) => setFontField(role, 'sizePt', e.target.value)}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 100 }}>
                          <TextField
                            size="small"
                            type="number"
                            fullWidth
                            value={font.lineHeight}
                            onChange={(e) => setFontField(role, 'lineHeight', e.target.value)}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 100 }}>
                          <TextField
                            size="small"
                            type="color"
                            fullWidth
                            value={font.color}
                            onChange={(e) => setFontField(role, 'color', e.target.value)}
                          />
                        </TableCell>
                        <TableCell sx={{ width: 160 }}>
                          <Stack direction="row" spacing={1}>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={font.bold}
                                  onChange={(e) => setFontField(role, 'bold', e.target.checked)}
                                />
                              }
                              label={t('bold')}
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={font.italic}
                                  onChange={(e) => setFontField(role, 'italic', e.target.checked)}
                                />
                              }
                              label={t('italic')}
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {dialogTab === 'rules' && (
            <Grid container spacing={2}>
              <Grid item xs={6} md={4}>
                {numberField(t('paragraphIndentCm'), form.paragraphIndentCm, (v) => setField('paragraphIndentCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subParagraphCm'), form.subParagraphIndentCm, (v) => setField('subParagraphIndentCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subSubParagraphCm'), form.subSubParagraphIndentCm, (v) => setField('subSubParagraphIndentCm', v), t('cm'))}
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('chapterStart')}
                  select
                  size="small"
                  fullWidth
                  value={form.chapterStartSide}
                  onChange={(e) => setField('chapterStartSide', e.target.value)}
                >
                  <MenuItem value="left">{t('leftPage')}</MenuItem>
                  <MenuItem value="right">{t('rightPage')}</MenuItem>
                  <MenuItem value="next">{t('nextPage')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('chapterAboveCm'), form.spacing.chapterTitleBeforeCm, (v) => setSpacingField('chapterTitleBeforeCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('chapterBelowCm'), form.spacing.chapterTitleAfterCm, (v) => setSpacingField('chapterTitleAfterCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subChapterAboveCm'), form.spacing.subChapterBeforeCm, (v) => setSpacingField('subChapterBeforeCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subChapterBelowCm'), form.spacing.subChapterAfterCm, (v) => setSpacingField('subChapterAfterCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subSubAboveCm'), form.spacing.subSubChapterBeforeCm, (v) => setSpacingField('subSubChapterBeforeCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('subSubBelowCm'), form.spacing.subSubChapterAfterCm, (v) => setSpacingField('subSubChapterAfterCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('textAboveCm'), form.spacing.textBeforeCm, (v) => setSpacingField('textBeforeCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('textBelowCm'), form.spacing.textAfterCm, (v) => setSpacingField('textAfterCm', v), t('cm'))}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.paragraphEndMarkerEnabled}
                      onChange={(e) => setField('paragraphEndMarkerEnabled', e.target.checked)}
                    />
                  }
                  label={t('paragraphEndMarker')}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <TextField
                  label={t('marker')}
                  size="small"
                  fullWidth
                  value={form.paragraphEndMarker}
                  onChange={(e) => setField('paragraphEndMarker', e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                {numberField(t('markerOffsetCm'), form.paragraphEndMarkerOffsetCm, (v) => setField('paragraphEndMarkerOffsetCm', v), t('cm'))}
              </Grid>
              <Grid item xs={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.flagSingleWordLastLine}
                      onChange={(e) => setField('flagSingleWordLastLine', e.target.checked)}
                    />
                  }
                  label={t('oneWord')}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.flagSingleLinePage}
                      onChange={(e) => setField('flagSingleLinePage', e.target.checked)}
                    />
                  }
                  label={t('oneLine')}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {editingId ? t('saveChanges') : t('createDesignTemplate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DesignTemplateManagement;
